import { useEffect, useMemo, useState } from "react";
import s from "@/components/Misc/PromoTimerBanner.module.scss";
import { setCookie, getCookie } from "@/utils/helpers";

const base = window?.post_template ?? {};
const megaSale = base.tr_mega_sale_label || "MEGA-SALE";
const timerLabel = base.tr_mega_sale_timer_label;
const promoAppliedLabel = base.promo_applied_label;
const megaSaleBannerIcon = base.mega_sale_banner_icon;

function safeInt(val, fallback) {
  const num = parseInt(val, 10);
  return Number.isFinite(num) && num >= 0 ? num : fallback;
}

const timerHours = safeInt(base.hours_remaining_number, 2);
const timerMinutes = safeInt(base.minutes_remaining_number, 13);
const timerSeconds = safeInt(base.seconds_remaning_number, 59);

const pad2 = (n) => String(n).padStart(2, "0");

function useCountdownCookie(totalSeconds) {
  const [remaining, setRemaining] = useState(Math.max(totalSeconds, 0));
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    let t = getCookie("checkout_timer");
    const now = Date.now();
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    if (!t || (t.cooldownExpires && now >= t.cooldownExpires)) {
      const endAt = now + totalSeconds * 1000;
      const cooldownExpires = now + ONE_DAY_MS;
      t = { endAt, cooldownExpires };
      setCookie("checkout_timer", t, cooldownExpires);
    }
    const timeLeft = Math.max(Math.floor((t.endAt - now) / 1000), 0);
    setRemaining(timeLeft);
    if (timeLeft === 0) {
      setIsFinished(true);
      return;
    }

    const start = Date.now();
    let prev = timeLeft;

    const id = setInterval(() => {
      const elapsed = Math.floor((Date.now() - start) / 1000);
      const next = Math.max(timeLeft - elapsed, 0);

      if (next !== prev) {
        prev = next;
        setRemaining(next);
      }
      if (next === 0) {
        setIsFinished(true);
        clearInterval(id);
      }
    }, 250);

    return () => clearInterval(id);
  }, [totalSeconds]);

  const hrs = Math.floor(remaining / 3600);
  const mins = Math.floor((remaining % 3600) / 60);
  const secs = remaining % 60;

  return { hrs, mins, secs, isFinished };
}

const CountdownUnit = ({ value, label }) => (
  <div className={s.promoBanner__unit}>
    <p className={s["promoBanner__unit--value"]}>{value}</p>
    <p className={s["promoBanner__unit--label"]}>{label}</p>
  </div>
);

const PromoTimerBanner = () => {
  const totalSeconds = useMemo(
    () => Math.max(0, timerHours * 3600 + timerMinutes * 60 + timerSeconds),
    [timerHours, timerMinutes, timerSeconds]
  );

  const { hrs, mins, secs, isFinished } = useCountdownCookie(totalSeconds);

  if (isFinished) return null;

  return (
    <div className={s.promoBanner}>
      <div className={s.promoBanner__wrapper}>
        <div className={s.promoBanner__text}>
          <div className={s["promoBanner__text--container"]}>
            <p className={s["promoBanner__text--sale"]}>
              <span className={s["promoBanner__text--icon"]}>
                <img src={megaSaleBannerIcon} alt="Mega Sale" />
              </span>
              {megaSale}
            </p>
            <div>
              <p className={s["promoBanner__text--promo"]}>{promoAppliedLabel}</p>
            </div>
          </div>
        </div>
        <div className={s.promoBanner__timer}>
          <p className={s["promoBanner__timer--label"]}>{timerLabel}</p>
          <CountdownUnit value={pad2(hrs)} label="HRS" />
          <div className={s["promoBanner__timer--separator"]}>:</div>
          <CountdownUnit value={pad2(mins)} label="MIN" />
          <div className={s["promoBanner__timer--separator"]}>:</div>
          <CountdownUnit value={pad2(secs)} label="SEC" />
        </div>
      </div>
    </div>
  );
};

export default PromoTimerBanner;
